"use client";

import { Star, UserPlus, Users } from "lucide-react";
import { usePathname } from "next/navigation";
import type { ReactNode } from "react";
import { useMemo } from "react";

import {
  InvitesCacheProvider,
  useInvitesCacheKey,
} from "@/app/(discover)/candidate/invites/contexts/invites-cache-context";
import { CountBadge } from "@/components/shared/count-badge";
import { type TabItem, TabNavigation } from "@/components/shared/navigation";
import { useCandidateCounts } from "@/hooks/data/use-candidate-counts";
import { useConnectionsCount } from "@/hooks/data/use-connections-count";
import { isHiringSeasonDormant } from "@/lib/dormant-season/resolve";
import routes from "@/lib/routes";
import {
  type InviteTabValue,
  inviteTabValueSchema,
} from "@/lib/types/invites.types";
import { useAuth } from "@/stores/use-auth";

const validTabs = inviteTabValueSchema.enum;

function useInviteTabs(): readonly TabItem[] {
  const { user } = useAuth();
  const userId = user?.id;

  const connectionsDormant = isHiringSeasonDormant("connections");
  const invitationsDormant = isHiringSeasonDormant("invitations");
  const recommendedDormant = isHiringSeasonDormant("recommended");
  const countsDormant = invitationsDormant && recommendedDormant;

  const { data: candidateCountsData } = useCandidateCounts(userId, {
    staleTime: 0, // refetches data on mount
    enabled: !!userId && !countsDormant,
  });

  const { data: connectionsData } = useConnectionsCount(userId, {
    staleTime: 0, // refetches data on mount
    enabled: !!userId && !connectionsDormant,
  });

  const pendingInvitationsCount = candidateCountsData?.invitations_count ?? 0;
  const connectionsCount = connectionsData?.count ?? 0;
  const recommendedInvitationsCount =
    candidateCountsData?.teacher_recommendations_count ?? 0;

  return useMemo(
    () =>
      [
        {
          value: validTabs.connections,
          label: "Connections",
          icon: Users,
          href: routes.getCandidateInvites("connections"),
          badge: connectionsDormant ? null : (
            <CountBadge count={connectionsCount} />
          ),
        },
        {
          value: validTabs.invitations,
          label: "Invitations",
          icon: UserPlus,
          href: routes.getCandidateInvites("invitations"),
          badge: invitationsDormant ? null : (
            <CountBadge count={pendingInvitationsCount} />
          ),
        },
        {
          value: validTabs.recommended,
          label: "Recommended",
          icon: Star,
          href: routes.getCandidateInvites("recommended"),
          badge: recommendedDormant ? null : (
            <CountBadge count={recommendedInvitationsCount} />
          ),
        },
      ] as const,
    [
      pendingInvitationsCount,
      connectionsCount,
      recommendedInvitationsCount,
      connectionsDormant,
      invitationsDormant,
      recommendedDormant,
    ],
  );
}

export function InvitesTabsLayoutView({
  tabs,
  activeTab,
  children,
  onTabClick,
}: {
  tabs: readonly TabItem[];
  activeTab: InviteTabValue;
  children: ReactNode;
  onTabClick?: () => void;
}) {
  return (
    <div className="max-page-height">
      <div className="flex flex-auto justify-center h-full pb-5 px-4 sm:px-6 lg:px-10 xl:px-16 2xl:px-24">
        <div className="flex flex-col max-w-[1280px] w-full h-full">
          <TabNavigation
            tabs={tabs}
            activeTab={activeTab}
            onTabClick={onTabClick}
          />
          {children}
        </div>
      </div>
    </div>
  );
}

function InvitesTabsLayoutInner({ children }: { children: ReactNode }) {
  const pathname = usePathname();
  const inviteTabs = useInviteTabs();
  const { invalidateInvitesCache } = useInvitesCacheKey();

  const activeTab: InviteTabValue =
    (inviteTabs.find((tab) => pathname.includes(tab.value))
      ?.value as InviteTabValue) ?? validTabs.invitations;

  return (
    <InvitesTabsLayoutView
      tabs={inviteTabs}
      activeTab={activeTab}
      onTabClick={invalidateInvitesCache}
    >
      {children}
    </InvitesTabsLayoutView>
  );
}

export default function InvitesTabsLayout({
  children,
}: {
  children: ReactNode;
}) {
  return (
    <InvitesCacheProvider>
      <InvitesTabsLayoutInner>{children}</InvitesTabsLayoutInner>
    </InvitesCacheProvider>
  );
}
